import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
    Table,
    TableHead,
    TableBody,
    TableContainer,
    TableCell,
    TableRow,
    TextField,
    Button,
    Box,
    IconButton,
    Typography,
    Grid,
    InputAdornment,
    FormControlLabel,
    Checkbox,
    CircularProgress,
    Autocomplete
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace';
import { AutoComplete } from 'primereact/autocomplete';
import { API_BASE } from '../../utils/api';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { formatCurrency } from "../../utils/currency";


function InventoryAdd() {
    const navigate = useNavigate();
    const { id } = useParams(); // Get batch ID from URL
    const isEditMode = !!id; // Check if we're in edit mode

    const [selectedProduct, setSelectedProduct] = useState(null);
    const [filteredProducts, setFilteredProducts] = useState([]);
    const [productDetails, setProductDetails] = useState(null);
    const [isActive, setIsActive] = useState(false);
    const [loading, setLoading] = useState(false);
    const [warehouseSuggestions, setWarehouseSuggestions] = useState([]);
    const [warehouseInput, setWarehouseInput] = useState("");

    const [batchData, setBatchData] = useState({
        stock: '',
        manufactureDate: '',
        expiryDate: '',
        warehouse: '',
        rack: ''
    });

    const getCurrencySymbol = () => {
        const formatted = formatCurrency(0);
        return formatted.replace(/[\d.,\s]/g, '');
    };

    // Fetch batch details when in edit mode
    useEffect(() => {
        if (isEditMode) {
            fetchBatchDetails();
        }
    }, [id]);

    const fetchBatchDetails = async () => {
        setLoading(true);
        try {
            const response = await axios.get(`${API_BASE}/api/batch/batchDetails/${id}`);
            const batch = response.data;

            // Set product
            setSelectedProduct({
                _id: batch.product._id,
                productName: batch.product.productName
            });

            // Set batch dates and status
            setBatchData({
                manufactureDate: batch.manufactureDate?.split('T')[0] || '',
                expiryDate: batch.expiryDate?.split('T')[0] || '',
                warehouse: batch.warehouse || '',
                rack: batch.rack || ''
            });
            setIsActive(batch.status === 'active');

            // Set product details with merged batch variants
            setProductDetails({
                _id: batch.product._id,
                productName: batch.product.productName,
                variants: batch.variants.map(v => ({
                    ...v,
                    // calculatedAvailable is already provided by backend
                    calculatedAvailable: v.calculatedAvailable || v.availableStock || 0
                }))
            });

        } catch (error) {
            console.error('Error fetching batch details:', error);
            toast.error('Error loading batch details');
        } finally {
            setLoading(false);
        }
    };

    const fetchWarehouseSuggestions = async (query) => {
        if (!query || query.length < 1) return;

        try {
            const response = await axios.get(`${API_BASE}/api/batch/warehouse-suggestions?query=${query}`);
            setWarehouseSuggestions(response.data);
        } catch (error) {
            console.error("Error fetching warehouse suggestions:", error);
        }
    };

    const searchProducts = async (event) => {
        try {
            const response = await axios.get(`${API_BASE}/api/products/search?query=${event.query}`);
            setFilteredProducts(response.data);
        } catch (error) {
            console.error('Error searching products:', error);
            toast.error('Error searching products');
        }
    };

    const onProductSelect = async (product) => {
        try {
            const response = await axios.get(`${API_BASE}/api/products/view/${product._id}`);
            setProductDetails(response.data);
            setSelectedProduct(product);
        } catch (error) {
            console.error('Error fetching product details:', error);
            toast.error('Error fetching product details');
        }
    };

    const handleBatchDataChange = (e) => {
        const { name, value } = e.target;
        setBatchData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!selectedProduct) {
            toast.warning('Please select a product');
            return;
        }
        if (!batchData.manufactureDate || !batchData.expiryDate) {
            toast.warning('Please fill manufacture and expiry dates');
            return;
        }

        const manufactureDate = new Date(batchData.manufactureDate);
        const expiryDate = new Date(batchData.expiryDate);
        const today = new Date();

        if (expiryDate <= manufactureDate) {
            toast.warning('Expiry date must be after manufacture date');
            return;
        }

        if (expiryDate < today.setHours(0, 0, 0, 0)) {
            toast.warning('Cannot save batch: expiry date is already passed');
            return;
        }
        const hasStock = productDetails.variants?.some(
            (v) => parseInt(v.newStock || 0) > 0
        );

        if (!hasStock && !isEditMode) {
            toast.warning('Please add stock for at least one variant');
            return;
        }

 const updatedVariants = productDetails.variants.map((v) => ({
  variantId: v._id,
  price: parseFloat(v.price) || 0,
  offerPrice: parseFloat(v.offerPrice) || 0,
  ...(isEditMode 
      ? { newStock: v.newStock ? parseInt(v.newStock) : null }
      : { stockToAdd: v.newStock ? parseInt(v.newStock) : 0 }
  )
}));



        const payload = {
            productId: selectedProduct._id,
            manufactureDate: batchData.manufactureDate,
            expiryDate: batchData.expiryDate,
            variants: updatedVariants,
            status: isActive ? 'active' : 'inactive',
            warehouse: batchData.warehouse,
            rack: batchData.rack,
        };

        try {
            if (isEditMode) {
                await axios.put(`${API_BASE}/api/batch/update-batch/${id}`, payload);
                toast.success('Batch updated successfully');
            } else {
                await axios.post(`${API_BASE}/api/batch/add-batch`, payload);
                toast.success('Batch added successfully');
            }

            setTimeout(() => navigate('/inventory'), 1500);
        } catch (error) {
            console.error(`Error ${isEditMode ? 'updating' : 'adding'} batch:`, error);

            const errorMessage = error.response?.data?.message ||
                `Error ${isEditMode ? 'updating' : 'adding'} batch`;

            toast.error(errorMessage);
        }
    };


    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <div className="container">
            <ToastContainer position="top-center" autoClose={3000} />

            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
                <IconButton
                    onClick={() => navigate('/inventory')}
                    style={{
                        backgroundColor: '#e0e0e0',
                        border: '1px solid #ccc',
                        borderRadius: '50%',
                        padding: '5px',
                        color: '#333',
                    }}
                >
                    <KeyboardBackspaceIcon fontSize="medium" />
                </IconButton>

                <Typography variant="h5" component="h1">
                    {isEditMode ? 'Edit Batch' : 'Add Batch'}
                </Typography>
            </div>

            {!isEditMode && (
                <Box mb={4}>
                    <Typography variant="h6" gutterBottom>
                        Search Product
                    </Typography>
                    <AutoComplete
                        value={selectedProduct}
                        suggestions={filteredProducts}
                        completeMethod={searchProducts}
                        field="productName"
                        onChange={(e) => {
                            setSelectedProduct(e.value);
                            if (!e.value) {
                                setProductDetails(null);
                            }
                        }}
                        onSelect={(e) => onProductSelect(e.value)}
                        placeholder="Select Product"
                        style={{ width: '100%' }}
                        inputStyle={{
                            width: '100%',
                            padding: '16px 12px',
                            fontSize: '1.1rem',
                            borderRadius: '4px'
                        }}
                    />
                </Box>
            )}

            {isEditMode && selectedProduct && (
                <Box mb={4}>
                    <Typography variant="h6" gutterBottom>
                        Product
                    </Typography>
                    <TextField
                        fullWidth
                        value={selectedProduct.productName}
                        disabled
                        variant="filled"
                        InputProps={{
                            readOnly: true,
                        }}
                    />
                </Box>
            )}

            {productDetails && (
                <>
                    <Box mb={4}>
                        <Typography variant="h6" gutterBottom>
                            Product Details
                        </Typography>
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Quantity</TableCell>
                                        <TableCell>Unit</TableCell>
                                        <TableCell>Price</TableCell>
                                        <TableCell>Offer Price</TableCell>
                                        <TableCell>{isEditMode ? ' Stock' : 'Stock '}</TableCell>
                                        <TableCell>Available Stock</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {productDetails.variants?.map((variant, index) => (
                                        <TableRow key={index}>
                                            <TableCell>{variant.quantity}</TableCell>
                                            <TableCell>{variant.unit}</TableCell>

                                            {/* Price */}
                                            <TableCell>
                                                <TextField
                                                    type="number"
                                                    size="small"
                                                    variant="standard"
                                                    value={variant.price}
                                                    onChange={(e) => {
                                                        const newVariants = [...productDetails.variants];
                                                        newVariants[index].price = e.target.value;
                                                        setProductDetails({ ...productDetails, variants: newVariants });
                                                    }}
                                                    InputProps={{
                                                        inputProps: { min: 0 },
                                                        startAdornment: (
                                                            <InputAdornment position="start">
                                                                {getCurrencySymbol()}
                                                            </InputAdornment>
                                                        ),
                                                    }}
                                                    style={{ width: '100px' }}
                                                />
                                            </TableCell>

                                            {/* Offer Price */}
                                            <TableCell>
                                                <TextField
                                                    type="number"
                                                    size="small"
                                                    variant="standard"
                                                    value={variant.offerPrice || ''}
                                                    onChange={(e) => {
                                                        const newVariants = [...productDetails.variants];
                                                        const offerValue = parseFloat(e.target.value || 0);
                                                        const basePrice = parseFloat(newVariants[index].price || 0);

                                                        if (offerValue > basePrice) {
                                                            toast.warning('Offer price cannot be greater than price');
                                                            return;
                                                        }

                                                        newVariants[index].offerPrice = e.target.value;
                                                        setProductDetails({ ...productDetails, variants: newVariants });
                                                    }}
                                                    InputProps={{
                                                        inputProps: { min: 0 },
                                                        startAdornment: (
                                                            <InputAdornment position="start">
                                                                {getCurrencySymbol()}
                                                            </InputAdornment>
                                                        ),
                                                    }}
                                                    style={{ width: '100px' }}
                                                />
                                            </TableCell>


                                            {/* Stock to Add / Current Stock */}
                                            <TableCell>
                                                <TextField
                                                    type="number"
                                                    size="small"
                                                    variant="standard"
                                                    placeholder={isEditMode ? "Add stock" : "Add quantity"}
                                                    value={variant.newStock || ''}
                                                    onChange={(e) => {
                                                        const newVariants = [...productDetails.variants];
                                                        const inputValue = e.target.value;
                                                        const enteredStock = parseInt(inputValue || 0);

                                                        newVariants[index].newStock = inputValue;

                                                        if (isEditMode) {
                                                            newVariants[index].calculatedAvailable = enteredStock;
                                                        } else {
                                                            const existingAvailable = parseInt(variant.availableStock || 0);
                                                            newVariants[index].calculatedAvailable = existingAvailable + enteredStock;
                                                        }

                                                        setProductDetails({ ...productDetails, variants: newVariants });
                                                    }}
                                                    inputProps={{ min: 0 }}
                                                    style={{ width: '100px' }}
                                                />
                                            </TableCell>

                                            <TableCell>
                                                {variant.calculatedAvailable ?? variant.availableStock ?? 0}
                                            </TableCell>


                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>

                    <Box mb={4}>
                        <Typography variant="h6" gutterBottom>
                            {isEditMode ? 'Batch Details' : 'Add Batch'}
                        </Typography>
                        <Grid container spacing={3}>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="Manufacture Date"
                                    name="manufactureDate"
                                    type="date"
                                    value={batchData.manufactureDate}
                                    onChange={handleBatchDataChange}
                                    variant="filled"
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="Expiry Date"
                                    name="expiryDate"
                                    type="date"
                                    value={batchData.expiryDate}
                                    onChange={handleBatchDataChange}
                                    variant="filled"
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Autocomplete
                                    freeSolo
                                    options={warehouseSuggestions}
                                    value={batchData.warehouse}
                                    inputValue={warehouseInput}
                                    onInputChange={(e, newInput) => {
                                        setWarehouseInput(newInput);
                                        setBatchData({ ...batchData, warehouse: newInput });
                                        fetchWarehouseSuggestions(newInput);
                                    }}
                                    onChange={(e, newValue) => {
                                        setBatchData({ ...batchData, warehouse: newValue || "" });
                                        setWarehouseInput(newValue || "");
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Warehouse"
                                            variant="filled"
                                            placeholder="Enter warehouse"
                                            fullWidth
                                            InputLabelProps={{ shrink: true }}
                                        />
                                    )}
                                />
                            </Grid>



                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="Rack"
                                    name="rack"
                                    value={batchData.rack}
                                    onChange={(e) => {
                                        const value = e.target.value;

                                        if (/^\d*$/.test(value)) {
                                            handleBatchDataChange(e);
                                        }
                                    }}
                                    variant="filled"
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>

                        </Grid>
                    </Box>

                    <Box mb={3}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={isActive}
                                    onChange={(e) => setIsActive(e.target.checked)}
                                    color="primary"
                                />
                            }
                            label="Active"
                        />
                    </Box>

                    <Box display="flex" justifyContent="flex-start">
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleSubmit}
                            size="large"
                        >
                            {isEditMode ? 'Update' : 'Save'}
                        </Button>
                    </Box>
                </>
            )}
        </div>
    );
}

export default InventoryAdd;